import React, { useMemo, useState, useEffect, useRef } from 'react';
import { cn } from '@/lib/utils';
import { Check, X } from 'lucide-react';

export const passwordStrengthCriteria = {
  minLength: 8,
  hasUppercase: /[A-Z]/.test.bind(/[A-Z]/),
  hasLowercase: /[a-z]/.test.bind(/[a-z]/),
  hasNumber: /[0-9]/.test.bind(/[0-9]/),
  hasSpecialChar: /[!@#$%^&*(),.?":{}|<>]/.test.bind(/[!@#$%^&*(),.?":{}|<>]/),
};

export interface PasswordRequirement {
  met: boolean;
  text: string;
}

export interface PasswordStrengthIndicatorProps extends React.HTMLAttributes<HTMLDivElement> {
  password: string;
  /**
   * Whether to show the requirements list
   * @default true
   */
  showRequirements?: boolean;
  /**
   * Whether to use a popover that shows when input is focused
   * @default false
   */
  usePopover?: boolean;
  /**
   * The input element that triggers the popover
   * Only used when usePopover is true
   */
  children?: React.ReactNode;
  /**
   * Classes for the popover content
   */
  popoverContentClassName?: string;
  /**
   * Classes for the popover wrapper
   */
  popoverWrapperClassName?: string;
  /**
   * ID of the input element to track focus
   * @default "newpassword"
   */
  inputId?: string;
  /**
   * Callback function to indicate whether all criteria are met
   */
  onValidationChange?: (isValid: boolean) => void;
}

/**
 * Check if all password requirements are met
 */
export function areAllRequirementsMet(password: string): boolean {
  if (!password) return false;

  return (
    password.length >= passwordStrengthCriteria.minLength &&
    passwordStrengthCriteria.hasUppercase(password) &&
    passwordStrengthCriteria.hasLowercase(password) &&
    passwordStrengthCriteria.hasNumber(password) &&
    passwordStrengthCriteria.hasSpecialChar(password)
  );
}

/**
 * Get all requirements with their status
 */
export function getPasswordRequirements(password: string): PasswordRequirement[] {
  return [
    {
      met: password.length >= passwordStrengthCriteria.minLength,
      text: 'At least 8 characters',
    },
    {
      met: passwordStrengthCriteria.hasUppercase(password),
      text: 'At least one uppercase letter',
    },
    {
      met: passwordStrengthCriteria.hasLowercase(password),
      text: 'At least one lowercase letter',
    },
    {
      met: passwordStrengthCriteria.hasNumber(password),
      text: 'At least one number',
    },
    {
      met: passwordStrengthCriteria.hasSpecialChar(password),
      text: 'At least one special character',
    },
  ];
}

/**
 * The password requirements display component
 */
export function PasswordRequirementsList({
  password,
  className,
}: {
  password: string;
  className?: string;
}) {
  const requirements = useMemo(() => getPasswordRequirements(password), [password]);

  return (
    <div className={cn('mt-2 text-xs text-gray-700', className)}>
      <ul className="space-y-1.5">
        {requirements.map((req, index) => (
          <li
            key={index}
            className="flex items-center gap-2"
          >
            <span
              className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full ${
                req.met ? 'bg-green-100' : 'bg-red-100'
              }`}
            >
              {req.met ? (
                <Check className="h-3 w-3 text-green-600" />
              ) : (
                <X className="h-3 w-3 text-red-600" />
              )}
            </span>
            <span className={req.met ? 'text-green-700' : 'text-gray-700'}>{req.text}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * A component that displays password requirements.
 * Can be used directly or with a popover that shows when the input is focused.
 */
export function PasswordRequirementsChecklist({
  password,
  showRequirements = true,
  className,
  usePopover = false,
  children,
  popoverContentClassName,
  popoverWrapperClassName,
  inputId = 'newpassword',
  onValidationChange,
  ...props
}: PasswordStrengthIndicatorProps) {
  const [showIndicator, setShowIndicator] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const prevValidationRef = useRef<boolean | undefined>(undefined);

  const requirementsValid = useMemo(() => areAllRequirementsMet(password), [password]);

  useEffect(() => {
    if (password.length > 0 && !requirementsValid) {
      setShowIndicator(true);
    } else {
      setShowIndicator(false);
    }
  }, [password, requirementsValid]);

  useEffect(() => {
    if (!usePopover) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setShowIndicator(false);
      }
    };

    setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 100);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [usePopover]);

  useEffect(() => {
    if (onValidationChange && password && prevValidationRef.current !== requirementsValid) {
      prevValidationRef.current = requirementsValid;
      onValidationChange(requirementsValid);
    } else if (!password) {
      prevValidationRef.current = undefined;
    }
  }, [requirementsValid, onValidationChange, password]);

  const IndicatorContent = () => (
    <div
      className={cn('mb-3 mt-1', className)}
      {...props}
    >
      {showRequirements && password && <PasswordRequirementsList password={password} />}
    </div>
  );

  if (usePopover && children) {
    return (
      <div
        className={cn('relative', popoverWrapperClassName)}
        ref={containerRef}
        onClick={() => {
          if (!requirementsValid) {
            setShowIndicator(true);
          }
        }}
      >
        {children}

        {/* Show the popover only when we should display it and requirements aren't met yet */}
        {showIndicator && password.length > 0 && !requirementsValid && (
          <div className="absolute left-0 z-50 mt-1 w-full">
            <div
              className={cn(
                'w-full rounded-md border bg-white p-4 shadow-md',
                popoverContentClassName,
              )}
            >
              <IndicatorContent />
            </div>
          </div>
        )}
      </div>
    );
  }

  return <IndicatorContent />;
}
